import { z } from "zod";

export const NativeAuthorizationRequestSchema = z
  .object({
    code_challenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    state: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/u),
    device_label: z.string().trim().min(1).max(120),
  })
  .strict();
export const NativeAuthorizationResponseSchema = z
  .object({
    id: z.uuid(),
    authorization_url: z.url(),
  })
  .strict();
export const NativeApproveRequestSchema = z.object({ request_id: z.uuid() }).strict();
export const NativeApproveResponseSchema = z
  .object({ callback_url: z.string().startsWith("oyakoflow://auth?") })
  .strict();
export const NativeExchangeRequestSchema = z
  .object({
    request_id: z.uuid(),
    code: z.string().min(32).max(128),
    code_verifier: z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/u),
  })
  .strict();
export const NativeRefreshRequestSchema = z
  .object({ refresh_token: z.string().min(32).max(256) })
  .strict();
export const NativeEnrollmentRequestSchema = z
  .object({ token: z.string().min(32).max(512) })
  .strict();
export const NativeEnrollmentPreviewSchema = z
  .object({
    child_name: z.string().min(1),
    workspace_name: z.string().min(1),
    device_label: z.string().min(1),
    expires_at: z.iso.datetime({ offset: true }),
  })
  .strict();
export const NativeTokenResponseSchema = z
  .object({
    access_token: z.string(),
    refresh_token: z.string(),
    token_type: z.literal("Bearer"),
    expires_in: z.number().int().min(1).max(900),
    workspace_id: z.uuid(),
    device_id: z.uuid(),
    actor_type: z.enum(["adult", "child"]),
    child_id: z.uuid().nullable(),
  })
  .strict();
export const NativePushRequestSchema = z
  .object({
    device_token: z.string().regex(/^[0-9a-fA-F]{64,512}$/u),
    environment: z.enum(["sandbox", "production"]),
  })
  .strict();
export const NativeSuccessSchema = z.object({ ok: z.literal(true) }).strict();

export const nativeSchemas = {
  NativeAuthorizationRequest: NativeAuthorizationRequestSchema,
  NativeAuthorizationResponse: NativeAuthorizationResponseSchema,
  NativeApproveRequest: NativeApproveRequestSchema,
  NativeApproveResponse: NativeApproveResponseSchema,
  NativeExchangeRequest: NativeExchangeRequestSchema,
  NativeRefreshRequest: NativeRefreshRequestSchema,
  NativeEnrollmentRequest: NativeEnrollmentRequestSchema,
  NativeEnrollmentPreview: NativeEnrollmentPreviewSchema,
  NativeTokenResponse: NativeTokenResponseSchema,
  NativePushRequest: NativePushRequestSchema,
  NativeSuccess: NativeSuccessSchema,
};
export function nativePaths() {
  const route = (
    operationId: string,
    input: string | null,
    output: string,
    authenticated = false,
  ) => ({
    post: {
      operationId,
      tags: ["native"],
      security: authenticated ? [{ bearerAuth: [] }, { cookieAuth: [] }] : [],
      parameters: [
        ...(authenticated
          ? [
              {
                name: "X-Workspace-Id",
                in: "header",
                required: true,
                schema: { type: "string", format: "uuid" },
              },
              { name: "X-CSRF-Token", in: "header", required: false, schema: { type: "string" } },
            ]
          : []),
        {
          name: "Idempotency-Key",
          in: "header",
          required: true,
          schema: { type: "string", minLength: 16, maxLength: 200 },
        },
      ],
      ...(input
        ? {
            requestBody: {
              required: true,
              content: {
                "application/json": { schema: { $ref: `#/components/schemas/${input}` } },
              },
            },
          }
        : {}),
      responses: {
        "200": {
          description: "Native client result",
          content: { "application/json": { schema: { $ref: `#/components/schemas/${output}` } } },
        },
        ...Object.fromEntries(
          [400, 401, 403, 409, 429, 503].map((status) => [
            String(status),
            {
              description: "Request rejected",
              content: {
                "application/json": { schema: { $ref: "#/components/schemas/ErrorEnvelope" } },
              },
            },
          ]),
        ),
      },
    },
  });
  return {
    "/api/v1/native/authorizations": route(
      "createNativeAuthorization",
      "NativeAuthorizationRequest",
      "NativeAuthorizationResponse",
    ),
    "/api/v1/native/authorizations/approve": route(
      "approveNativeAuthorization",
      "NativeApproveRequest",
      "NativeApproveResponse",
      true,
    ),
    "/api/v1/native/token": route(
      "exchangeNativeCode",
      "NativeExchangeRequest",
      "NativeTokenResponse",
    ),
    "/api/v1/native/refresh": route(
      "refreshNativeToken",
      "NativeRefreshRequest",
      "NativeTokenResponse",
    ),
    "/api/v1/native/enroll/inspect": route(
      "inspectNativeEnrollment",
      "NativeEnrollmentRequest",
      "NativeEnrollmentPreview",
    ),
    "/api/v1/native/enroll": route(
      "enrollNativeChild",
      "NativeEnrollmentRequest",
      "NativeTokenResponse",
    ),
    "/api/v1/native/logout": route("logoutNativeSession", null, "NativeSuccess", true),
    "/api/v1/native/push": route("registerNativePush", "NativePushRequest", "NativeSuccess", true),
    "/api/v1/native/push/disable": route("disableNativePush", null, "NativeSuccess", true),
  };
}
